import * as angular from "angular";
import { CampaignDataProvider } from "../../data-provider/campaign.dp";
import { Campaign } from "../../models/campaign.model";
import { DialogUtils } from "../../helper/dialog.helper";
import { ProspectorDataProvider } from "../../data-provider/prospector.dp";
import { PolygonService } from "../map/polygon.helper";
import { prospectorFilters } from "../../utils/constants/prospector.constants";
import { ToastUtils } from "../../helper/toast.helper";
import { FilterDataProvider } from "../../data-provider/filter.dp";
import { PermissionCheck } from "../../shared/permission";

let ProspectorFormComponent = {
    selector: "vcProspectorForm",
    templateUrl:
        "/angular/components/prospector/prospector-form.component.html",
    bindings: {
        map: "=",
        open: "=",
        leadsImported: "&",
    },
    controller: class ProspectorFormController {
        // * bindings for typescript compiler *
        public map: any;
        public open: boolean;
        public leadsImported: any;
        // ************************************

        private $scope: any;
        private $window: any;
        private readonly dialog: DialogUtils;
        private readonly $timeout: any;
        private readonly $interval: any;
        private readonly toast: ToastUtils;
        public activeCampaign: Campaign;
        public dataProvider: ProspectorDataProvider;
        public filterDataProvider: FilterDataProvider;
        public count: number = null;
        public loading: boolean = true;
        public loadProgress: number = 0;
        public loadProgressInterval: any;
        public importing: boolean;
        public polygonService: PolygonService;
        public filterOptions: any;
        public appliedFilters: any = {};
        public maxZoom: number = 4;
        public savedFilters: any[];
        public selectedState: string;
        private importsInterval: any;
        public imports: any[] = [];
        public suggestedState: any = null;
        public credits: any;
        public firstLoad: boolean = true;
        public creditsDelay: any;
        public maxSingleImport: number = 10000;
        private readonly permissionProvider: PermissionCheck;

        constructor(
            $scope: any,
            $window: any,
            $interval: any,
            $timeout: any,
            DialogUtils: DialogUtils,
            CampaignDataProvider: CampaignDataProvider,
            ProspectorDataProvider: ProspectorDataProvider,
            FilterDataProvider: FilterDataProvider,
            PolygonService: PolygonService,
            ToastUtils: ToastUtils,
            PermissionCheck: PermissionCheck
        ) {
            this.$scope = $scope;
            this.$window = $window;
            this.$timeout = $timeout;
            this.$interval = $interval;
            this.dialog = DialogUtils;
            this.dataProvider = ProspectorDataProvider;
            this.filterDataProvider = FilterDataProvider;
            this.polygonService = PolygonService;
            this.activeCampaign = CampaignDataProvider.getActiveCampaign();
            this.permissionProvider = PermissionCheck;
            this.toast = ToastUtils;

            $scope.$on("polygonUpdated", (event, widget) => {
                this.getCount(true);
            });

            this.filterOptions = prospectorFilters;

            this.dataProvider.getImports().then((imports) => {
                this.imports = imports;
            });

            this.dataProvider.credits().then((credits) => {
                this.credits = credits;
                this.getCount(true);
            });
        }

        $onInit() {
            this.$timeout(() => {
                this.$scope.$watch(
                    () => this.map.bounds,
                    (newValue: object, oldValue: object) => {
                        if (newValue && oldValue && newValue != oldValue) {
                            this.getCount(this.firstLoad);
                        }
                    },
                    true
                );
                this.$scope.$watch(
                    () => this.dataProvider.imports,
                    (newValue: object, oldValue: object) => {
                        if (newValue && oldValue && newValue != oldValue) {
                            if (this.creditsDelay) {
                                this.creditsDelay = this.$interval.cancel(
                                    this.creditsDelay
                                );
                            }
                            this.creditsDelay = this.$interval((): void => {
                                this.$interval.cancel(this.creditsDelay);

                                this.dataProvider
                                    .credits()
                                    .then((imports: any[]) => {
                                        this.credits = imports;
                                    });
                            }, 1000);
                        }
                    },
                    true
                );
            }, 200);

            this.filterDataProvider.saved().then((filters) => {
                this.savedFilters = filters.filter((filter: any) => {
                    return filter.type == "prospector";
                });
            });

            this.selectedState = this.dataProvider.getSelectedState();

            if (!this.selectedState) {
                this.openStateDialog();
            }
        }

        openIntercom(): void {
            this.$window.Intercom(
                "showNewMessage",
                "Hello, I would like to purchase more Prospector credits please"
            );
        }

        checkWrongStateSelected(): void {
            this.dataProvider
                .getSuggestedState(this.map.center)
                .then((state) => {
                    if (state && state.abbreviation) {
                        this.suggestedState = state;
                        this.dataProvider.setSelectedState(this.selectedState);
                    }
                });
        }

        hasEnoughCredits(newLeads: number): boolean {
            if (this.credits && this.credits.available) {
                return newLeads <= this.credits.available;
            }
            return false;
        }

        getFilterName(key: any): string {
            for (let filterCategory in this.filterOptions) {
                for (let filterKey in this.filterOptions[filterCategory][
                    "list"
                ]) {
                    if (key == filterKey) {
                        return this.filterOptions[filterCategory]["list"][
                            filterKey
                        ]["name"];
                    }
                }
            }
        }

        removeFilter(key: any): void {
            if (this.appliedFilters[key]) {
                delete this.appliedFilters[key];
            }
            this.getCount();
        }

        clearFilters(filter: any): void {
            for (let key in this.appliedFilters) {
                if (key != "points") {
                    delete this.appliedFilters[key];
                }
            }
        }

        setFilter(filter: any): void {
            this.appliedFilters = JSON.parse(filter.filters);

            for (let filterCategory in this.filterOptions) {
                for (let key in this.filterOptions[filterCategory]["list"]) {
                    this.filterOptions[filterCategory]["list"][key]["open"] =
                        this.isActiveFilter(key);
                }
            }

            this.getCount();
        }

        import(): void {
            if (!this.importing && !this.loading) {
                this.importing = true;
                this.dataProvider.import(this.prepareRequestData()).then(
                    (data: any) => {
                        this.leadsImported();
                        this.toast.success(
                            "Import started, shortly you will see your houses appear on the map",
                            null,
                            null,
                            null,
                            "bottom right"
                        );
                        if (this.polygonService.selectedPolygon) {
                            this.polygonService.discardDrawing();
                        }
                        this.dataProvider.startProgress(data.imid);

                        this.dataProvider.credits().then((imports: any[]) => {
                            this.credits = imports;
                            this.importing = false;
                        });
                    },
                    () => {
                        this.importing = false;
                    }
                );
            }
        }

        getCount(noDelay: boolean = false): void {
            if (
                this.hasEnoughCredits(0) &&
                this.dataProvider.getSelectedState() &&
                this.map.zoom >= this.maxZoom &&
                this.hasSearchArea()
            ) {
                this.loading = true;
                this.suggestedState = null;
                this.startProgressBar();
                this.firstLoad = false;

                this.dataProvider
                    .count(this.prepareRequestData(), noDelay)
                    .then(
                        (data: any): void => {
                            if (data != "user cancelled") {
                                this.count = data;

                                if (
                                    this.count == 0 &&
                                    Object.keys(this.appliedFilters).length <= 1 //make sure no filters selected
                                ) {
                                    this.checkWrongStateSelected();
                                }

                                this.loading = false;
                                this.$interval.cancel(
                                    this.loadProgressInterval
                                );
                            }
                        },
                        (error: any): void => {
                            this.loading = false;
                            this.$interval.cancel(this.loadProgressInterval);
                        }
                    );
            } else {
                this.loading = false;
            }
        }

        changeState(state: string): void {
            this.dataProvider.setSelectedState(state);
            this.getCount(true);
        }

        saveFilterDialog(ev: any): void {
            let confirm = this.dialog
                .prompt()
                .title("Save this filter?")
                .textContent(
                    "If you save this filter you can quickly load it again."
                )
                .placeholder("Filter name")
                .required(true)
                .targetEvent(ev)
                .ok("Save Filter")
                .cancel("Cancel");

            this.dialog
                .showPreConfiguredDialog(confirm)
                .then((filterName: string) => {
                    this.filterDataProvider
                        .create(filterName, this.appliedFilters, "prospector")
                        .then((filter: any) => {
                            this.savedFilters.unshift(filter);
                            this.toast.success("Filter saved.");
                        });
                });
        }

        startProgressBar(): void {
            this.loadProgress = 7;
            this.loadProgressInterval = this.$interval(() => {
                if (Math.random() > 0.5) {
                    let amountRemaining: number = (100 - this.loadProgress) / 2;
                    if (amountRemaining > 20) amountRemaining = 20;
                    let increaseBy: number = Math.floor(
                        Math.random() * amountRemaining
                    );
                    this.loadProgress += increaseBy;

                    if (this.loadProgress > 95) {
                        this.loadProgress = 95;
                    }
                }
            }, 700);
        }

        toggle(value: any, key: string, filter: any): void {
            if (!this.appliedFilters[key] || !this.appliedFilters[key].values) {
                this.appliedFilters[key] = { values: [], type: filter.type };
            }
            let idx = this.appliedFilters[key].values.indexOf(value);
            if (idx > -1) {
                this.appliedFilters[key].values.splice(idx, 1);
            } else {
                this.appliedFilters[key].values.push(value);
            }
            if (
                !this.appliedFilters[key].values ||
                this.appliedFilters[key].values.length == 0
            ) {
                delete this.appliedFilters[key];
            }
            this.getCount();
        }

        exists(value: any, key: string): boolean {
            if (!this.appliedFilters[key] || !this.appliedFilters[key].values) {
                return false;
            }
            return this.appliedFilters[key].values.indexOf(value) > -1;
        }

        countActiveFilters(filters: any): number {
            let numActive: number = 0;
            for (let key in filters) {
                if (key != "points" && this.isActiveFilter(key)) {
                    numActive += 1;
                }
            }
            return numActive;
        }

        isActiveFilter(key: string): boolean {
            if (this.appliedFilters[key]) {
                let filter = this.appliedFilters[key];
                if (filter.values) {
                    if (filter.values.length > 0) {
                        return true;
                    } else if (
                        (filter.values.min && filter.values.min != "") ||
                        (filter.values.max && filter.values.max != "")
                    ) {
                        return true;
                    } else if (
                        (filter.values.from && filter.values.from != "") ||
                        (filter.values.to && filter.values.to != "")
                    ) {
                        return true;
                    }
                }
            }

            return false;
        }

        getAppliedFilters(): any[] {
            return this.appliedFilters;
        }

        setAppliedFilters(filterName, filter: any): any[] {
            let activeFilters = [];
            if (filter.list) {
                for (const key in filter.list) {
                    const obj = filter.list[key];
                    this.setAppliedFilters(key, obj);
                }
            } else {
                if (
                    filter.values &&
                    filter.type == "checklist" &&
                    filter.values.length > 0
                ) {
                    this.appliedFilters[filterName] = {
                        values: filter.values.map((obj) => {
                            if (
                                angular.isDefined(obj.value) &&
                                obj.value != null
                            ) {
                                return obj.value;
                            } else {
                                return obj.name;
                            }
                        }),
                        type: filter.type,
                    };
                } else if (
                    filter.values &&
                    filter.type == "boolean" &&
                    filter.values.length > 0
                ) {
                    this.appliedFilters[filterName] = {
                        values: filter.values,
                        type: filter.type,
                    };
                } else if (
                    filter.type == "number" &&
                    (filter.min_val || filter.max_val)
                ) {
                    let values: any = {};
                    if (filter.min_val) {
                        values.min = filter.min_val;
                    }
                    if (filter.max_val) {
                        values.max = filter.max_val;
                    }
                    this.appliedFilters[filterName] = {
                        values: values,
                        type: filter.type,
                    };
                } else if (
                    filter.type == "date" &&
                    (filter.from || filter.to)
                ) {
                    let values: any = {};
                    if (filter.from) {
                        values.from = filter.from;
                    }
                    if (filter.to) {
                        values.to = filter.to;
                    }
                    this.appliedFilters[filterName] = {
                        values: values,
                        type: filter.type,
                    };
                }
            }
            return activeFilters;
        }

        getMinDate(fieldType: string, filter: any): any {
            if (fieldType == "min") {
                return filter.min;
            } else if (fieldType == "max") {
                return filter.from ?? filter.min;
            }
        }

        getMaxDate(fieldType: string, filter: any): any {
            if (fieldType == "min") {
                return filter.to ?? filter.max;
            } else if (fieldType == "max") {
                return filter.max;
            }
        }

        prepareRequestData(): any {
            this.selectedState = this.dataProvider.getSelectedState();
            let filters: any = this.getAppliedFilters();
            filters.points = { values: this.getPoints(), type: "spacial" };
            return {
                filters: filters,
                state: this.selectedState,
                count: this.count,
            };
        }

        openStateDialog($event: any = null): any {
            this.dialog
                .show({
                    component: "vc-prospector-change-state-dialog",
                    targetEvent: $event,
                    scope: this.$scope,
                    size: "med",
                    locals: {
                        center: this.map.center,
                    },
                })
                .then(
                    () => {
                        if (this.dataProvider.getSelectedState()) {
                            this.getCount(true);
                            let stateObj =
                                this.dataProvider.getStateFromAbbreviation(
                                    this.dataProvider.getSelectedState()
                                );
                            this.map.center = {
                                latitude: stateObj.latitude,
                                longitude: stateObj.longitude,
                            };
                            this.map.zoom = this.dataProvider.getZoomFromKm(
                                stateObj.width_km
                            );
                        } else {
                            this.close();
                        }
                    },
                    () => {
                        if (!this.dataProvider.getSelectedState()) {
                            this.close();
                        }
                    }
                );
        }

        purchaseAddOn($event: any): any {
            this.dialog
                .show({
                    component: "vc-prospector-add-on-dialog",
                    targetEvent: $event,
                    scope: this.$scope,
                    size: "large",
                    locals: {},
                })
                .then(
                    () => {},
                    () => {}
                );
        }

        // The map populates bounds on its first idle event, which can land
        // after this panel opens; without an area to search, getPoints()
        // would throw and leave the progress bar spinning forever. The
        // bounds watcher in $onInit re-runs getCount once bounds arrive.
        hasSearchArea(): boolean {
            if (this.polygonService.selectedPolygon) {
                return true;
            }
            return !!(
                this.map.bounds &&
                this.map.bounds.northeast &&
                this.map.bounds.southwest
            );
        }

        getPoints(): any {
            if (this.polygonService.selectedPolygon) {
                return this.polygonService.selectedPolygon.points;
            } else {
                return this.getPointsFromBounds(
                    this.map.bounds.northeast,
                    this.map.bounds.southwest
                );
            }
        }

        getPointsFromBounds(northEast: any, southWest: any): any {
            return [
                {
                    latitude: northEast.latitude,
                    longitude: northEast.longitude,
                },
                {
                    latitude: southWest.latitude,
                    longitude: northEast.longitude,
                },
                {
                    latitude: southWest.latitude,
                    longitude: southWest.longitude,
                },
                {
                    latitude: northEast.latitude,
                    longitude: southWest.longitude,
                },
            ];
        }

        close(): void {
            this.importsInterval = this.$interval.cancel(this.importsInterval);
            this.open = false;
        }
    },
};

angular
    .module("prospector", [])
    .component(ProspectorFormComponent.selector, ProspectorFormComponent);
